/**
 * Overlay de fase de la sala online: gobierna el ciclo de vida visible al
 * jugador. El host ve los controles (EMPEZAR / REANUDAR / FINALIZAR); el
 * invitado ve el estado ("esperando al anfitrión", "en pausa"). Se oculta
 * mientras se juega (phase "playing") y en "over" (lo cubre la pantalla de
 * resultado).
 */
import type { RoomPhase } from "@tak/sim";
import { injectThemeTokens } from "../theme";

/* Los tokens de paleta (--v-*) viven en :root (src/theme.ts, inyectados por
   injectThemeTokens()). Antes este namespace hardcodeaba los mismos hex (y con
   una deriva: líneas a 0.2 de opacidad frente a 0.18 en el resto). */
const CSS = `
.ph-root{position:fixed;inset:0;z-index:19;display:none;align-items:center;justify-content:center;
  padding:var(--sp-4);font-family:var(--tb-font-ui);color:var(--tb-text);
  background:radial-gradient(130% 110% at 50% -10%,rgba(16,26,38,0.86),rgba(4,6,10,0.94));
  backdrop-filter:blur(0.4rem);-webkit-backdrop-filter:blur(0.4rem);}
.ph-root.open{display:flex;}
.ph-card{position:relative;width:min(28rem,100%);padding:var(--sp-6) var(--sp-5);text-align:center;
  background:linear-gradient(158deg,var(--tb-panel),var(--tb-abyss));box-shadow:var(--tb-lift);}
.ph-t{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.3em;
  font-weight:800;color:var(--tb-muted);}
.ph-h{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;font-size:var(--fs-lg);
  letter-spacing:0.06em;font-weight:900;margin:var(--sp-2) 0 var(--sp-4);}
.ph-btn{width:100%;margin-top:var(--sp-2);}
.ph-wait{font-size:var(--fs-xs);color:var(--tb-muted);letter-spacing:0.06em;margin-top:var(--sp-2);}
`;

export interface PhaseOverlayCallbacks {
  onStart: () => void;
  onResume: () => void;
  onFinish: () => void;
}

export class PhaseOverlay {
  private root: HTMLDivElement;
  private cb: PhaseOverlayCallbacks | null = null;

  constructor() {
    injectThemeTokens();
    if (!document.getElementById("ph-style")) {
      const s = document.createElement("style");
      s.id = "ph-style";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    this.root = document.createElement("div");
    this.root.className = "ph-root";
    document.body.appendChild(this.root);
  }

  setCallbacks(cb: PhaseOverlayCallbacks): void { this.cb = cb; }

  /** Refresca según la fase y si el jugador es el host. */
  update(phase: RoomPhase, isHost: boolean): void {
    // La cuenta atrás NO se tapa con un panel: se dibuja en la cancha, grande y
    // centrada, porque es un momento de juego y hay que estar mirando el campo.
    if (phase === "playing" || phase === "over" || phase === "countdown") { this.hide(); return; }
    this.root.innerHTML = "";
    const card = document.createElement("div");
    card.className = "ph-card tb-cut tb-edge";

    if (phase === "lobby") {
      card.innerHTML = `<div class="ph-t">SALA</div><div class="ph-h">${isHost ? "SALA LISTA" : "ESPERANDO"}</div>`;
      if (isHost) {
        // La invitación se copia desde el botón minimalista del menú de pausa
        // (el host gobierna la sala desde ahí); el overlay ya no duplica el link.
        card.appendChild(this.button("▶ EMPEZAR PARTIDO", () => this.cb?.onStart()));
      } else {
        card.appendChild(this.wait("Esperando a que el anfitrión empiece…"));
      }
    } else { // paused
      card.innerHTML = `<div class="ph-t">SALA</div><div class="ph-h">EN PAUSA</div>`;
      if (isHost) {
        card.appendChild(this.button("▶ REANUDAR", () => this.cb?.onResume()));
        card.appendChild(this.button("🏁 FINALIZAR PARTIDO", () => this.cb?.onFinish(), true));
      } else {
        card.appendChild(this.wait("El anfitrión pausó la partida."));
      }
    }

    this.root.appendChild(card);
    this.root.classList.add("open");
  }

  private button(label: string, onClick: () => void, ghost = false): HTMLElement {
    const b = document.createElement("button");
    b.className = "ph-btn tb-btn tb-cut-sm " + (ghost ? "ghost" : "primary");
    b.textContent = label;
    b.onclick = onClick;
    return b;
  }

  private wait(text: string): HTMLElement {
    const d = document.createElement("div");
    d.className = "ph-wait";
    d.textContent = text;
    return d;
  }

  /** ¿Está tapando la pantalla? (ver `covered` en main.ts) */
  get isOpen(): boolean {
    return this.root.classList.contains("open");
  }

  hide(): void {
    this.root.classList.remove("open");
    this.root.innerHTML = "";
  }
}
